import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { AdminCatalogForm } from "../../types";
import {
  selectAdminCatalogForm,
  selectIsAddModalOpen,
} from "../../redux/selector";
import { adminCatalogActions } from "../../redux/slice";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof AdminCatalogForm;
  label: string;
  type: "text" | "textarea";
};

const ROWS: FieldConfig[][] = [
  [{ key: "catalogName", label: "CATALOG_NAME", type: "text" }],
  [{ key: "description", label: "DESCRIPTION", type: "textarea" }],
  [
    { key: "amountOfItems", label: "AMOUNT_OF_ITEMS", type: "text" },
    { key: "mainInfluenceField", label: "MAIN_INFLUENCE_FIELD", type: "text" },
  ],
  [{ key: "visualIntegration", label: "VISUAL_INTEGRATION", type: "text" }],
];

const REQUIRED_FIELDS: Array<keyof AdminCatalogForm> = [
  "catalogName",
  "description",
  "amountOfItems",
  "mainInfluenceField",
  "visualIntegration",
];

const P = "CATALOGS.MODAL";

const AddCatalogModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectAdminCatalogForm);

  const onClose = () => {
    dispatch(adminCatalogActions.closeAddModal());
  };

  const setField = (name: keyof AdminCatalogForm, value: string) =>
    dispatch(adminCatalogActions.setFormField({ name, value }));

  const placeholder = (label: string) => t(`${P}.PLACEHOLDERS.ENTER_${label}`);

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const renderField = ({ key, label, type }: FieldConfig) => (
    <Box key={key} className={styles.field}>
      <Typography className={styles.label}>{t(`${P}.FIELDS.${label}`)}</Typography>
      <TextField
        className={type === "textarea" ? styles.descriptionInput : styles.input}
        fullWidth
        multiline={type === "textarea"}
        variant="outlined"
        placeholder={placeholder(label)}
        value={form[key]}
        InputProps={{ notched: false }}
        onChange={(event) => setField(key, event.target.value)}
      />
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {ROWS.map((row, index) =>
            row.length > 1 ? (
              <Box key={index} className={styles.grid}>
                {row.map(renderField)}
              </Box>
            ) : (
              renderField(row[0])
            )
          )}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid}
            onClick={() => dispatch(adminCatalogActions.addCatalog())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCatalogModal;
